"use client";

import Filters from "@/src/components/common/Search/Filter";
import SearchBar from "@/src/components/common/Search/SearchBar";
import SearchResults from "@/src/components/common/Search/Results";
import SearchResultsSummary from "@/src/components/common/Search/Toolbar";
import { Box } from "@mui/material";
import { FormMasthead } from "@/src/components/common/broadsheet/form-kit";
import {
  doubleRule,
  SHEET_GUTTER,
} from "@/src/components/common/broadsheet/tokens";
import { getBaseUrlForCSR } from "@/src/utils/fetch-helpers";
import { useDebounce } from "@/src/hooks/useDebounce";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { stripHTMLTags } from "@/src/utils/strip-html-tags";
import { isValidDate } from "@/src/utils/is-valid-date";

const Search = ({
  journal,
  journals,
}: {
  journal: Journal;
  journals: Journal[];
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const ARTICLES_PER_PAGE = 10;

  const DEFAULT_SEARCH_FIELDS: Record<string, boolean> = {
    title: true,
    keywords: true,
    authors: true,
    doi: false,
    abstract: true,
    body: false,
    references: false,
  };

  const initialFilters = {
    journal:
      journal && !journal.is_main_site
        ? [String(journal.id)]
        : ([] as string[]),
    startDate: undefined as Date | undefined,
    endDate: undefined as Date | undefined,
    volume: "",
    issue: "",
  };

  const [filters, setFilters] = useState(initialFilters);
  const [sortBy, setSortBy] = useState(
    () => searchParams.get("sort_by") ?? "none",
  );
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get("search_term") ?? "",
  );
  const [searchFields, setSearchFields] = useState(() => {
    const requestedParts = searchParams.get("search_parts");
    if (!requestedParts) {
      return DEFAULT_SEARCH_FIELDS;
    }

    const requested = requestedParts.split(",");

    return Object.fromEntries(
      Object.keys(DEFAULT_SEARCH_FIELDS).map((field) => [
        field,
        requested.includes(field),
      ]),
    ) as Record<string, boolean>;
  });
  const [selectedArticles, setSelectedArticles] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentJournal, setCurrentJournal] = useState<any>();
  const [results, setResults] = useState({
    items: [] as any[],
    total: 0,
    didYouMean: [],
    pageNumber: 1,
    pageSize: ARTICLES_PER_PAGE,
  });
  const [loading, setLoading] = useState(false);
  const lastFetchParams = useRef<string>("");

  useEffect(() => {
    if (journal && !journal.is_main_site) {
      setCurrentJournal({
        id: journal.id,
        value: journal.id,
        label: journal.title,
      });
    }
  }, [journal]);

  const buildSearchParams = useCallback(() => {
    const params = new URLSearchParams();

    if (searchQuery) {
      params.append("search_term", stripHTMLTags(searchQuery));
    }

    const activeFields = Object.entries(searchFields)
      .filter(([_, isActive]) => isActive)
      .map(([field, _]) => field);

    if (activeFields.length > 0) {
      params.append("search_parts", activeFields.join(","));
    }

    if (filters.journal.length > 0) {
      params.append("filter[journal_id]", filters.journal.join(","));
    }

    if (isValidDate(filters.startDate)) {
      params.append(
        "filter[date_from]",
        filters.startDate.toISOString().split("T")[0],
      );
    }

    if (isValidDate(filters.endDate)) {
      params.append(
        "filter[date_to]",
        filters.endDate.toISOString().split("T")[0],
      );
    }

    if (filters.volume) {
      params.append("filter[volume]", filters.volume);
    }

    if (filters.issue) {
      params.append("filter[issue]", filters.issue);
    }

    params.append("sort_by", sortBy);
    params.append("page[number]", currentPage.toString());
    params.append("page[size]", ARTICLES_PER_PAGE.toString());

    return params;
  }, [searchQuery, searchFields, filters, sortBy, currentPage]);

  const fetchSearchResults = useCallback(async () => {
    const searchParams = buildSearchParams();
    const paramsString = searchParams.toString();

    if (lastFetchParams.current === paramsString) {
      return;
    }

    lastFetchParams.current = paramsString;
    setLoading(true);
    try {
      const url = `${getBaseUrlForCSR()}/api/search?${paramsString}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      const data = await response.json();

      if (data.data) {
        setResults({
          items: data.data.items || [],
          total: data.data.total || 0,
          didYouMean: data.data.did_you_mean || [],
          pageNumber: data.data.page_number || 1,
          pageSize: data.data.page_size || ARTICLES_PER_PAGE,
        });
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      setResults({
        items: [],
        total: 0,
        didYouMean: [],
        pageNumber: 1,
        pageSize: ARTICLES_PER_PAGE,
      });
    } finally {
      setLoading(false);
    }
  }, [buildSearchParams]);

  const [debouncedFetchSearchResults, cancelDebounce] = useDebounce(
    fetchSearchResults,
    500,
  );

  useEffect(() => {
    const hasActiveFilters =
      filters.journal.length > 0 ||
      filters.startDate ||
      filters.endDate ||
      filters.volume ||
      filters.issue;

    if (searchQuery || hasActiveFilters) {
      debouncedFetchSearchResults();
    } else {
      setResults({
        items: [],
        total: 0,
        didYouMean: [],
        pageNumber: 1,
        pageSize: ARTICLES_PER_PAGE,
      });
    }

    return () => {
      cancelDebounce();
    };
  }, [
    searchQuery,
    searchFields,
    filters,
    sortBy,
    currentPage,
    debouncedFetchSearchResults,
    cancelDebounce,
    router,
  ]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedArticles([]);
  }, [searchQuery, searchFields, filters, sortBy]);

  useEffect(() => {
    const params = new URLSearchParams();
    const isSearching =
      !!searchQuery ||
      filters.journal.length > 0 ||
      !!filters.startDate ||
      !!filters.endDate ||
      !!filters.volume ||
      !!filters.issue;

    if (searchQuery) {
      params.append("search_term", searchQuery);
    }

    const activeFields = Object.entries(searchFields)
      .filter(([_, isActive]) => isActive)
      .map(([field, _]) => field);
    if (isSearching && activeFields.length > 0) {
      params.append("search_parts", activeFields.join(","));
    }

    if (filters.journal.length > 0) {
      params.append("filter[journal_id]", filters.journal.join(","));
    }

    if (isValidDate(filters.startDate)) {
      params.append(
        "filter[date_from]",
        filters.startDate.toISOString().split("T")[0],
      );
    }

    if (isValidDate(filters.endDate)) {
      params.append(
        "filter[date_to]",
        filters.endDate.toISOString().split("T")[0],
      );
    }

    if (filters.volume) {
      params.append("filter[volume]", filters.volume);
    }

    if (filters.issue) {
      params.append("filter[issue]", filters.issue);
    }

    if (sortBy !== "none") {
      params.append("sort_by", sortBy);
    }

    const query = params.toString();
    const newUrl = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;

    if (`${window.location.pathname}${window.location.search}` !== newUrl) {
      router.replace(newUrl, { scroll: false });
    }
  }, [searchQuery, searchFields, filters, sortBy, router]);

  const paginatedArticles = useMemo(() => {
    return results.items;
  }, [results.items]);

  const handleFilterChange = (filterName: keyof typeof filters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleSelectAllOnPage = (checked: boolean | "indeterminate") => {
    const pageDois = paginatedArticles.map((a) => a.doi);
    if (checked) {
      setSelectedArticles((prev: any) => [...new Set([...prev, ...pageDois])]);
    } else {
      setSelectedArticles((prev: any) =>
        prev.filter((doi: string) => !pageDois.includes(doi)),
      );
    }
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setSelectedArticles([]);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(results.total / ARTICLES_PER_PAGE);

  const isAllOnPageSelected =
    paginatedArticles.length > 0 &&
    paginatedArticles.every((article) =>
      selectedArticles.includes(article.doi),
    );
  const isSomeOnPageSelected = paginatedArticles.some((article) =>
    selectedArticles.includes(article.doi),
  );

  const selectAllState = isAllOnPageSelected
    ? true
    : isSomeOnPageSelected
      ? "indeterminate"
      : false;

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 0.75, md: 1 }, ...doubleRule }}
      >
        <FormMasthead title="Advanced Search" />
      </Box>
      <SearchBar
        didYouMean={results.didYouMean}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchFields={searchFields}
        setSearchFields={setSearchFields}
      />
      <Filters
        currentJournal={currentJournal}
        journals={journals}
        filters={filters}
        sortBy={sortBy}
        onFilterChange={handleFilterChange}
        onSortByChange={setSortBy}
        onResetFilters={resetFilters}
      />
      <SearchResultsSummary
        sortedAndFilteredArticles={results.items}
        paginatedArticles={paginatedArticles}
        selectedArticles={selectedArticles}
        setSelectedArticles={setSelectedArticles}
        selectAllState={selectAllState}
        isAllResultsSelected={false}
        isSomeOnPageSelected={isSomeOnPageSelected}
        handleSelectAllOnPage={handleSelectAllOnPage}
        totalResults={results.total}
        loading={loading}
      />
      <SearchResults
        paginatedArticles={paginatedArticles}
        selectedArticles={selectedArticles}
        setSelectedArticles={setSelectedArticles}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
      />
    </Box>
  );
};

export default Search;
